import { useRef, useState } from "react";

import {
  createChatAgentSession,
  createChatAgentMessage,
  updateChatAgentSession,
} from "../../graphql/mutations";
import { iUser } from "../../interfaces/Auth/iUser";
import { generateClient } from "aws-amplify/api";
import { chatAgentMessagesByChatSessionId, chatAgentSessionsByProyectoId } from "../../graphql/queries";

interface ChatSession {
  id: string;
  userId: string;
  modelName: string;
  proyectoId?: string;
  resultadoId?: string;
  createdAt?: string;
  updatedAt?: string;
}

const client = generateClient();

export const useChatSession = (user: iUser) => {
  const [session, setSession] = useState<ChatSession | null>(null);
  const [isSessionLoading, setIsSessionLoading] = useState(false);
  const sessionRef = useRef<string | null>(null);
  
  // ================================================================
  // Crear sesión inicial de chat
  // ================================================================
 {/**  const startChatSession = async (
    modelName: string,
    proyectoId?: string,
    resultadoId?: string
  ) => {
    if (!user?.id) throw new Error("Usuario no autenticado");

    try {
      setIsSessionLoading(true);

      const result: any = await client.graphql({
        query: createChatAgentSession,
        variables: {
          input: {
            userId: user.id,
            modelName,
            proyectoId,
            resultadoId,
          },
        },
      });
proyecto id: 87e1c7bf784b46c184ba5ecf720261c9
sesión chat: 79f9fc0a-95f2-40d5-b2c9-94f3818848bf
      const newSession = result.data.createChatAgentSession;
      setSession(newSession);
      sessionRef.current = newSession.id;      
      console.log("Chat session creada:", newSession);
      return newSession;
    } catch (error) {
      console.error("Error en startChatSession:", error);
      throw error;
    } finally {
      setIsSessionLoading(false);
    }
  };*/}

  const startChatSession = async (
    modelName: string,
    proyectoId?: string,
    resultadoId?: string,
  ) => {
    if (!user?.id) throw new Error("Usuario no autenticado");

    // Si ya tenemos una sesión activa en memoria, la reutilizamos
    if (sessionRef.current && session) {
      return session;
    }

    try {
      setIsSessionLoading(true);

      let existingSession: ChatSession | null = null;

      // Si hay proyectoId, buscamos sesiones asociadas a ese proyecto
      if (proyectoId) {
        const result: any = await client.graphql({
          query: chatAgentSessionsByProyectoId,
          variables: { proyectoId },
        });

        const items: ChatSession[] =
          result?.data?.chatAgentSessionsByProyectoId?.items ?? [];

        if (items.length > 0) {
          const sameUserAndModel = items.filter(
            (s) => s.userId === user.id && s.modelName === modelName
          );

          const candidates =
            sameUserAndModel.length > 0 ? sameUserAndModel : items;

          // Se toma la más reciente (por updatedAt o createdAt)
          candidates.sort((a, b) => {
            const aTime = new Date(a.updatedAt ?? a.createdAt ?? 0).getTime();
            const bTime = new Date(b.updatedAt ?? b.createdAt ?? 0).getTime();
            return bTime - aTime; // DESC
          });

          existingSession = candidates[0];
        }
      }

      // Si ya había una sesión para ese proyecto se reutiliza
      if (existingSession) {
        setSession(existingSession);
        sessionRef.current = existingSession.id;
        console.log("Reutilizando sesión de chat:", existingSession);
        return existingSession;
      }

      // Si NO hay sesión para el proyecto se crea una nueva
      const resultCreate: any = await client.graphql({
        query: createChatAgentSession,
        variables: {
          input: {
            userId: user.id,
            modelName,
            proyectoId,
            resultadoId,
          },
        },
      });

      const newSession: ChatSession =
        resultCreate.data.createChatAgentSession;

      setSession(newSession);
      sessionRef.current = newSession.id;
      console.log("Chat session creada:", newSession);
      return newSession;
    } catch (error) {
      console.error("Error en startChatSession:", error);
      throw error;
    } finally {
      setIsSessionLoading(false);
    }
  };

  // ================================================================
  // Registrar mensajes
  // ================================================================
  const addChatMessage = async (
    actor: "user" | "assistant",
    content: string="",
    textoInstruccion:string="",
    tokensUsed?: number
  ) => {
    console.log("addChatMessage:", { actor, content, tokensUsed });    
    const sessionId = session?.id ?? sessionRef.current;

    if (!sessionId) {
      console.warn("No hay sesión activa para registrar mensaje");
      return;
    }

    try {
      const result: any = await client.graphql({
        query: createChatAgentMessage,
        variables: {
          input: {
            chatSessionId: sessionId,
            instruction: textoInstruccion,
            actor,
            content,
            tokensUsed,
          },
        },
      });
      console.log("Mensaje registrado:", result.data.createChatAgentMessage);
    } catch (error) {
      console.error("Error al crear ChatAgentMessage:", error);
    }
  };

  // ================================================================
  // Actualizar resumen de sesión (tokens, créditos, etc.)
  // ================================================================
  const updateSession = async (updateData: any) => {
    const sessionId = session?.id ?? sessionRef.current;
    if (!sessionId) {
      console.warn("No hay sesión activa para actualizar");
      return;
    }

    try {
      const result: any = await client.graphql({
        query: updateChatAgentSession,
        variables: {
          input: { id: sessionId, ...updateData },
        },
      });
      console.log("Sesión actualizada:", result.data.updateChatAgentSession);
    } catch (error) {
      console.error("Error al actualizar ChatAgentSession:", error);
    }
  };

  return { session, isSessionLoading, startChatSession, addChatMessage, updateSession };
};


// ================================================================
// Obtener sesiones de chat por proyectoId
// ================================================================
export const getChatsSessionsByProyectoId = async (proyectoId: string) => {
  try {
    const result: any = await client.graphql({
      query: chatAgentSessionsByProyectoId,
      variables: { proyectoId },
    });
    return result.data.chatAgentSessionsByProyectoId.items;
  } catch (error) {
    console.error("Error en getChatsSessionsByProyectoId:", error);
    throw error;
  }
}


// ================================================================
// Obtener mensajes de una sesión de chat
// ================================================================
export const getChatSession = async (sessionId: string) => {  
    try {
      const result: any = await client.graphql({
        query: chatAgentMessagesByChatSessionId,
        variables: { chatSessionId: sessionId },
      });
      return result.data.chatAgentMessagesByChatSessionId.items;
    } catch (error) {
      console.error("Error en getChatSession:", error);
      throw error;
    }
  };